import type { DisplayProduct } from './types';

/** The subset of a product this decision actually needs — so mock/partial rows
 *  (bespoke tenant landings build their own) can call it without a full
 *  `DisplayProduct`. */
export type ProductHrefInput = Pick<DisplayProduct, 'id'> &
  Partial<Pick<DisplayProduct, 'isVirtual' | 'replicatedPageSlug'>>;

export type ProductHrefOptions = {
  /** `tenant_config.features.virtual_products_open_replicated_page`. */
  openReplicatedPage: boolean;
  /** The sponsor ref captured from the replicated link (`getStoredRef()`). */
  storedRef?: string;
};

/**
 * Where a storefront product card should link.
 *
 * Normally `/product/:id`. When the tenant turned on
 * `virtual_products_open_replicated_page`, a VIRTUAL product that has an
 * ENABLED campaign page assigned (admin product editor → "Replicated page")
 * instead opens that campaign page, so a service/virtual offer sells through
 * its landing page rather than a cart-first product page.
 *
 * Two campaign URLs, because sponsor attribution has to survive:
 *   - a known ref → `/r/<ref>/<slug>`, the canonical shareable form
 *   - no ref yet  → `/c/<slug>`, which renders the same page and falls back to
 *     whatever ref is in sessionStorage at signup time
 *
 * Every other case keeps `/product/:id`: flag off, physical product, no page
 * assigned, or a page that is disabled/deleted (the server nulls the slug in
 * both of those, so this function only ever sees a live page).
 *
 * The campaign URL carries `?buy=<productId>` so the page knows WHICH product
 * sent the visitor: one page can be assigned to several products, and without
 * it the page's CTA can only offer signup — the buyer would sign up and land on
 * a dashboard with no trace of the thing they clicked. `replicated.tsx` forwards
 * it into the CTA, which then routes through `/buy/:productId`.
 */
export function productHref(p: ProductHrefInput, opts: ProductHrefOptions): string {
  const slug = p.replicatedPageSlug;
  if (!opts.openReplicatedPage || !p.isVirtual || !slug) return `/product/${p.id}`;
  const ref = opts.storedRef?.trim();
  const buy = `?buy=${encodeURIComponent(p.id)}`;
  return ref
    ? `/r/${encodeURIComponent(ref)}/${encodeURIComponent(slug)}${buy}`
    : `/c/${encodeURIComponent(slug)}${buy}`;
}
